import * as Sentry from '@sentry/react-native';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';

import { SuccessModal } from '@components/MobileOnboarding/SuccessModal';
import { RateAppModal } from '@components/Profile/RateAppModal';
import { useApolloClient } from '@hooks/useApolloClient';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import {
  markAppRated,
  markRateAppPromptDismissed,
  markRateAppPromptShown,
  openStoreReviewPage,
  recordRateAppTrigger,
  suppressRateAppPrompt,
  RateAppTriggerKey,
} from '@utils/rateAppPrompt';

type ActivePrompt = {
  source: RateAppTriggerKey;
  promptNumber: number;
};

type RecordTriggerOptions = {
  delayMs?: number;
  /**
   * When the FIRST prompt for this trigger becomes due, show the "Nice
   * Work!" celebration modal first; the rate-app modal follows when the
   * user taps "Keep Going". Later prompt stages skip the celebration.
   */
  celebrateFirstPrompt?: boolean;
  /**
   * How many occurrences this event represents (default 1) — e.g. assigning
   * 3 people in one action counts 3 toward the trigger's threshold.
   */
  count?: number;
};

type RateAppPromptContext = {
  /**
   * Records one occurrence of a trigger event and shows the rate-app modal
   * when the trigger's escalating schedule is due (see
   * `@utils/rateAppPrompt`). Fire-and-forget: storage errors are reported to
   * Sentry, never thrown. No-op on web — the modal deep-links to the app
   * stores.
   */
  recordTrigger: (
    trigger: RateAppTriggerKey,
    options?: RecordTriggerOptions
  ) => void;
};

const rateAppPromptContext = createContext<RateAppPromptContext | undefined>(
  undefined
);

export const RateAppPromptProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { Provider } = rateAppPromptContext;
  const [showPrompt, setShowPrompt] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  // The prompt currently on screen (or queued behind the celebration) —
  // Yes/Later analytics need its source and prompt number.
  const activePromptRef = useRef<ActivePrompt | null>(null);
  const pendingPromptRef = useRef<ActivePrompt | null>(null);
  // True while a prompt is visible, queued on a timer, or waiting behind
  // the celebration — further due triggers are dropped meanwhile. Safe to
  // drop: the stage only advances when a prompt is actually shown, so a
  // dropped trigger simply fires again on its next event.
  const promptQueuedRef = useRef(false);
  // True once the rate-app modal has actually reached the screen this
  // session. Unlike promptQueuedRef, this never resets — the modal may
  // show at most once per session across all triggers (TA-9183).
  const sessionPromptShownRef = useRef(false);
  const { t } = useTranslation();
  const { trackEvent } = useMixpanel();
  const { client } = useApolloClient();

  useEffect(() => () => clearTimeout(showTimerRef.current), []);

  const openPrompt = useCallback(
    (prompt: ActivePrompt) => {
      activePromptRef.current = prompt;
      sessionPromptShownRef.current = true;
      // The stage advances only now that the prompt actually reaches the
      // screen — recording alone never consumes a prompt
      if (client) {
        markRateAppPromptShown(client, prompt.source).catch((e) => {
          Sentry.captureException(e);
        });
      }
      trackEvent(MIXPANEL_EVENTS.RATE_US_MODAL_SHOWN, {
        source: prompt.source,
        prompt_number: prompt.promptNumber,
      });
      setShowPrompt(true);
    },
    [trackEvent, client]
  );

  const recordTrigger = useCallback(
    (
      trigger: RateAppTriggerKey,
      {
        delayMs = 0,
        celebrateFirstPrompt = false,
        count = 1,
      }: RecordTriggerOptions = {}
    ) => {
      if (Platform.OS === 'web' || !client) return;
      recordRateAppTrigger(client, trigger, Date.now(), count)
        .then(({ shouldPrompt, stage, count: currentCount, promptStage }) => {
          // Fires on every increment (not just at thresholds) for funnel
          // analysis — TA-9185
          trackEvent(MIXPANEL_EVENTS.RATE_US_TRIGGER_HIT, {
            trigger,
            current_count: currentCount,
            prompt_stage: promptStage,
          });
          if (!shouldPrompt) return;
          // At most one modal per session, across all triggers
          if (sessionPromptShownRef.current) return;
          // One prompt at a time; dropped triggers keep their stage and
          // fire again on the next event
          if (promptQueuedRef.current) return;
          promptQueuedRef.current = true;
          const prompt: ActivePrompt = {
            source: trigger,
            promptNumber: stage,
          };
          const show =
            celebrateFirstPrompt && stage === 1
              ? () => {
                  pendingPromptRef.current = prompt;
                  setShowCelebration(true);
                }
              : () => openPrompt(prompt);
          if (delayMs > 0) {
            showTimerRef.current = setTimeout(() => {
              showTimerRef.current = undefined;
              show();
            }, delayMs);
          } else {
            show();
          }
        })
        .catch((e) => {
          Sentry.captureException(e);
        });
    },
    [trackEvent, openPrompt, client]
  );

  const handleKeepGoing = () => {
    setShowCelebration(false);
    if (pendingPromptRef.current) {
      openPrompt(pendingPromptRef.current);
      pendingPromptRef.current = null;
    } else {
      setShowPrompt(true);
    }
  };

  const handleRateApp = async () => {
    trackEvent(MIXPANEL_EVENTS.RATE_US_LOVE_IT_TAPPED, {
      source: activePromptRef.current?.source,
      prompt_number: activePromptRef.current?.promptNumber,
    });
    setShowPrompt(false);
    promptQueuedRef.current = false;
    try {
      // Must complete before openStoreReviewPage — that call can background
      // or suspend the app, so a fire-and-forget write here could be lost
      if (client && activePromptRef.current) {
        await markRateAppPromptDismissed(
          client,
          activePromptRef.current.source
        );
      }
      await openStoreReviewPage();
      if (client) {
        await markAppRated(client);
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const handleLater = () => {
    // The third dismissal permanently suppresses the prompt (see
    // rateAppPrompt PROMPT_STAGES)
    const wasFinalPrompt = (activePromptRef.current?.promptNumber ?? 0) >= 3;
    trackEvent(MIXPANEL_EVENTS.RATE_US_LATER_TAPPED, {
      source: activePromptRef.current?.source,
      prompt_number: activePromptRef.current?.promptNumber,
      was_final_prompt: wasFinalPrompt,
    });
    if (client && activePromptRef.current) {
      // The re-prompt window is anchored to dismissal time, not show time
      markRateAppPromptDismissed(client, activePromptRef.current.source).catch(
        (e) => {
          Sentry.captureException(e);
        }
      );
    }
    if (wasFinalPrompt && client) {
      suppressRateAppPrompt(client).catch((e) => {
        Sentry.captureException(e);
      });
    }
    setShowPrompt(false);
    promptQueuedRef.current = false;
  };

  return (
    <Provider value={{ recordTrigger }}>
      {children}
      {Platform.OS !== 'web' && (
        <>
          {showCelebration && (
            <SuccessModal
              showConfetti={true}
              title={t('shared:niceWorkModal.title')}
              description={t('shared:niceWorkModal.message')}
              primaryLabel={t('shared:niceWorkModal.keepGoing')}
              onDismiss={() => {
                // Dropping the pending prompt is safe: its stage wasn't
                // advanced, so the next trigger event re-queues it
                pendingPromptRef.current = null;
                promptQueuedRef.current = false;
                setShowCelebration(false);
              }}
              onPrimary={handleKeepGoing}
            />
          )}

          <RateAppModal
            visible={showPrompt}
            onClose={handleLater}
            onConfirm={handleRateApp}
          />
        </>
      )}
    </Provider>
  );
};

export const useRateAppPrompt = (): RateAppPromptContext => {
  const context = useContext(rateAppPromptContext);
  if (context === undefined) {
    throw new Error('useRateAppPrompt must be used within a Provider');
  }
  return context;
};
